import { ActivatedRoute } from '@angular/router';
import { AppConfig } from '@app/app.config';
import { AuthenticationService } from '@app/security/shared/authentication.service';
import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import {
  ConnectionsService,
  DispatcherConnectionStatus
} from '@app/server/shared/connections.service';
import { DomSanitizer } from '@angular/platform-browser';
import { FormService } from '@app/forms/shared/forms.service';
import { LegacyService } from '@app/legacy/legacy.service';
import { Logger } from '@app/logger/logger';
import { LoginStatusModel } from '@app/security/shared/login-status-change.model';
import { LoginStatusService } from '@app/security/shared/login-status.service';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { OkDialogComponent } from '@app/shared/dialogs/ok-dialog.component';
import { RoutingStrategyService } from '@app/shared/services/routing-strategy.service';
import { SeatTrackerService } from '@app/security/shared/seat-tracker.service';
import { SecurityService } from '@app/security/shared/security.service';
import { SignalRDiagnosticsService } from '@app/server/shared/signalr-diagnostics.service';
import { ThemeService } from '@app/shared/services/theme.service';
import { TranslateWrapperService } from '@app/shared/services/translate-wrapper.service';
import { URL_PARAM_HIDE_MENU, WindowService } from '@app/shared/services/browser/window.service';
import { VersionService } from '@app/shared/services/version/version.service';
import { environment } from '@env/environment';
import { faThumbtack, faOutdent, faIndent, faSendBackward } from '@fortawesome/pro-solid-svg-icons';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { Observable } from 'rxjs';
import { ConnectivityService, ConnectivityStatus } from '@app/server/shared/connectivity.service';

const lgSfx = '\t(app.component)';

@Component({
  selector: 'acs-root',
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html',
  standalone: false
})
export class AppComponent implements OnDestroy {
  @ViewChild('spinnerElement') spinnerElement: ElementRef;

  public adminOnly: boolean;
  public connectivityStatus$: Observable<ConnectivityStatus>;
  public connectivityStatuses = ConnectivityStatus;
  public dispatcherConnectionStatus$: Observable<DispatcherConnectionStatus>;
  public dispatcherConnectionStatuses = DispatcherConnectionStatus;
  public hasSignalRErrors$: Observable<boolean>;
  public isShowingRouteLoadIndicator = false;
  public pinColor = '#808080';
  public showMenu = true;
  public title = 'Access Unlimited JS';
  public thumbTackIcon = faThumbtack;
  public toggleMenuIcon = this.showMenu ? faOutdent : faIndent;
  public userLoggedIn = false;
  public versionChanged = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private appConfig: AppConfig,
    private authService: AuthenticationService,
    private connectivityService: ConnectivityService,
    private connectionsService: ConnectionsService,
    private domSanitizer: DomSanitizer,
    private formService: FormService,
    private iconRegistry: MatIconRegistry,
    private legacyService: LegacyService,
    private logger: Logger,
    private loginStatusService: LoginStatusService,
    private matDialog: MatDialog,
    private routingStrategyService: RoutingStrategyService,
    private seatTrackerService: SeatTrackerService,
    private securityService: SecurityService,
    private signalRDiagnostics: SignalRDiagnosticsService,
    private themeService: ThemeService,
    private translate: TranslateWrapperService,
    private versionService: VersionService,
    private windowService: WindowService
  ) {
    this.connectivityService.setApiBaseUrl(this.appConfig.getServerUri());
    this.connectivityStatus$ = this.connectivityService.connectivityStatus$;
    this.dispatcherConnectionStatus$ = this.connectionsService.dispatcherConnectionStatus$;
    this.hasSignalRErrors$ = this.signalRDiagnostics.hasErrors$;
    this.initVersionService();
    this.themeService.initialize();
    this.setIconsInRegistry();

    this.logger.info(`Running environment ${environment.envName}`);

    this.translate.initializeLanguage();
    this.initializeRouting();

    this.logger.log(`app component loading ${lgSfx}`);
    this.userLoggedIn = this.securityService.getUserLoggedIn();
    this.connectionsService.connectToServer();

    this.loginStatusService.loginStatus.subscribe((loginStatus) => {
      this.onLoginStatusChange(loginStatus);

      this.adminOnly = this.authService.getOnlyAdminStatus();
    });

    this.initWindowSizeInfo();
    this.setKeepAliveEvents();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleClose(event): boolean {
    const form = this.formService.getCurrentForm();

    if (form && form.isDirty) {
      // returnValue = false is required for chromium even though it's deprecated (Jan 2023)
      // event.preventDefault() is required for firefox
      event.preventDefault();
      event.returnValue = false;
      return false;
    }
  }

  @HostListener('window:offline')
  public onNetworkOffline(): void {
    this.connectivityService.setBrowserOnline(false);
  }

  @HostListener('window:online')
  public onNetworkOnline(): void {
    this.connectivityService.setBrowserOnline(true);
    this.connectivityService.verifyApiConnection();
  }

  public openInNewWindow(): void {
    this.windowService.openCurrentViewInNewTab(this.formService.getCurrentForm());
  }

  public retryDispatcherConnection(): void {
    this.connectionsService.retryDispatcherConnection();
  }

  public exportSignalRDiagnostics(): void {
    // Export the in-memory buffer without changing the current connection state.
    this.signalRDiagnostics.export();
  }

  public dismissSignalRError(): void {
    this.signalRDiagnostics.dismissErrorNotification();
  }

  public toggleWindowPin(): void {
    this.windowService.toggleWindowPin();

    if (this.windowService.isCurrentWindowPinned()) {
      this.pinColor = 'primary';
    } else {
      this.pinColor = '';
    }
  }

  public toggleMenu(): void {
    this.showMenu = !this.showMenu;
    this.toggleMenuIcon = this.showMenu ? faOutdent : faIndent;
  }

  public ngOnDestroy(): void {
    this.logger.log(`app.component being destroyed ${lgSfx}`);
  }

  private onLoginStatusChange(loginStatus: LoginStatusModel): void {
    this.userLoggedIn = loginStatus.loggedIn;
    if (!this.userLoggedIn) {
      this.legacyService.useLegacyIntercom();
    }
  }

  private initWindowSizeInfo(): void {
    this.windowService.screenSize.screenHeight = document.documentElement.clientHeight;
    this.windowService.screenSize.screenWidth = document.documentElement.clientWidth;
    this.windowService.onWindowResize().subscribe((value) => {
      this.logger.log('Window resize event occurred', value);
    });
  }

  private setKeepAliveEvents(): void {
    this.seatTrackerService.checkSeatToken();
    this.seatTrackerService.KeepDbSessionAlive();
  }

  // This allows us to set up pro icons to work with Mat-Icon
  private setIconsInRegistry(): void {
    const svgSendBackward = icon(faSendBackward).html.join('');

    this.iconRegistry.addSvgIconLiteral(
      'send-backwards',
      this.domSanitizer.bypassSecurityTrustHtml(svgSendBackward)
    );
  }

  private initializeRouting(): void {
    this.routingStrategyService.initialize();

    this.routingStrategyService.showRouteLoadIndicator$.subscribe((val: boolean) => {
      this.isShowingRouteLoadIndicator = val;
    });

    this.routingStrategyService.titleChanged$.subscribe((val: string) => {
      this.title = val;
    });

    this.activatedRoute.queryParams.subscribe((params) => {
      if (params[URL_PARAM_HIDE_MENU] === '1') {
        this.showMenu = false;
        this.toggleMenuIcon = this.showMenu ? faOutdent : faIndent;
      }
    });
  }

  private initVersionService(): void {
    this.versionService.start();

    this.versionService.versionChanged$.subscribe({
      next: () => {
        this.versionChanged = true;
        this.matDialog.open(OkDialogComponent, {
          data: {
            title: 'Update Available',
            content: 'A new version of the app is available please refresh your browser.',
            disableClose: true
          }
        });
      }
    });
  }
}
